/**
 * Shipping protection toggle.
 *
 * Adds or removes the hidden protection variant from the cart. The amount
 * charged is set server side by the Cart Transform function; this element only
 * owns the line's presence and the shopper's opt-out.
 */
(function () {
  if (customElements.get("shipping-protection")) return;

  // True while our own cart update is in flight. Reconciling against the
  // markup that request is about to replace is the race this file has always
  // had to avoid.
  let inFlight = false;

  customElements.define(
    "shipping-protection",
    class ShippingProtection extends HTMLElement {
      connectedCallback() {
        this.variantId = this.dataset.variantId;
        this.sectionId = this.dataset.sectionId;
        this.inDrawer = this.hasAttribute("data-in-drawer");
        this.active = this.hasAttribute("data-active");
        this.quantity = parseInt(this.dataset.quantity, 10) || 0;
        this.optedOut = this.hasAttribute("data-opted-out");
        this.hasPhysical = this.hasAttribute("data-has-physical");
        this.busy = false;

        const button = this.querySelector("[data-sp-toggle]");
        if (button) button.addEventListener("click", this.onToggle.bind(this));
      }

      /**
       * Keeps the cart honest without the shopper doing anything: adds the line
       * when protection is on by default, and drops it once the cart no longer
       * holds anything that ships.
       */
      reconcile() {
        if (this.active && !this.hasPhysical) return this.setQuantity(0, null);
        if (!this.active && this.hasPhysical && !this.optedOut) {
          return this.setQuantity(1, null);
        }
        // One line, always. The fee is the whole cart's, not a per-unit charge,
        // so a line that ended up holding several of them is pulled back to one.
        // The function already divides the fee by the quantity, which keeps the
        // total right meanwhile but loses up to half a cent per unit to rounding.
        //
        // What drives it above one is still unknown: nothing in the cart, the
        // drawer or the product page reproduces it, yet live orders keep
        // arriving with it. The correction leaves a breadcrumb on the cart so an
        // affected order says where it happened, not just that it did.
        if (this.active && this.quantity > 1) {
          return this.setQuantity(1, null, {
            _shipping_protection_qty: this.anomalyNote(),
          });
        }
      }

      /**
       * Short enough to read at a glance on the order, and underscore prefixed
       * so Shopify keeps it off the shopper's confirmation.
       */
      anomalyNote() {
        let from = "-";
        try {
          if (document.referrer) from = new URL(document.referrer).pathname;
        } catch (error) {
          // A referrer we cannot parse tells us nothing; the rest still does.
        }
        return [
          "qty=" + this.quantity,
          "at=" + window.location.pathname,
          "from=" + from,
          "drawer=" + (this.inDrawer ? "1" : "0"),
          new Date().toISOString(),
        ].join(" ");
      }

      onToggle() {
        if (this.busy) return;
        return this.active
          ? this.setQuantity(0, "true")
          : this.setQuantity(1, "");
      }

      /**
       * Everything goes through /cart/update.js: it sets an exact quantity
       * instead of incrementing, and it is the only cart endpoint that accepts
       * attributes in the same request. `optOut` of null leaves the shopper's
       * stored choice untouched.
       */
      setQuantity(quantity, optOut, extraAttributes) {
        this.busy = true;
        inFlight = true;
        this.setAttribute("aria-busy", "true");

        const targets = this.sectionsToRender();
        const payload = { updates: {} };
        payload.updates[this.variantId] = quantity;
        if (optOut !== null) {
          payload.attributes = { shipping_protection_opt_out: optOut };
        }
        if (extraAttributes) {
          payload.attributes = Object.assign(
            {},
            payload.attributes,
            extraAttributes,
          );
        }
        payload.sections = [...new Set(targets.map((t) => t.section))];
        payload.sections_url = window.location.pathname;

        return fetch(
          window.routes.cart_update_url,
          Object.assign({}, webvista.fetchConfig(), {
            body: JSON.stringify(payload),
          }),
        )
          .then((response) => response.json())
          .then((json) => {
            if (json.status || json.errors) {
              throw new Error(json.description || json.message);
            }
            this.renderSections(json.sections, targets);
          })
          .catch(() => {
            // The cart is the source of truth; a reload beats a wrong toggle.
            // Only for a click: the reload runs reconcile again, so an automatic
            // add that keeps failing would reload the page forever.
            if (optOut !== null) window.location.reload();
          })
          .finally(() => {
            this.busy = false;
            inFlight = false;
            this.removeAttribute("aria-busy");
          });
      }

      renderSections(rendered, targets) {
        if (!rendered) return;
        targets.forEach((target) => {
          const html = rendered[target.section];
          if (!html) return;
          const source = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(target.selector);
          const host = document.getElementById(target.id);
          const destination =
            host && (host.querySelector(target.selector) || host);
          if (source && destination) destination.innerHTML = source.innerHTML;
        });
      }

      /**
       * The cart page and the drawer can both be present, and a toggle in one
       * must not leave the other showing a stale state. Only targets that are
       * actually on the page are requested.
       *
       * Every target is looked up inside its own id, the way the theme's cart
       * code does it. `.shopify-section` is what to read from the rendered
       * bubble; looked up on the page it is whichever section comes first.
       */
      sectionsToRender() {
        const targets = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Bottom-Cart-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];

        document.querySelectorAll("shipping-protection").forEach((element) => {
          const inDrawer = element.hasAttribute("data-in-drawer");
          const id = inDrawer ? "Cart-Drawer" : "Main-Cart";
          if (targets.some((target) => target.id === id)) return;
          targets.push(
            {
              id: id,
              section: element.dataset.sectionId,
              selector: inDrawer ? "#Cart-Drawer-Details" : "#Main-Cart-Details",
            },
            {
              id: id,
              section: element.dataset.sectionId,
              selector: ".cart-count",
            },
          );
        });

        return targets;
      }
    },
  );

  /**
   * Reconciling from connectedCallback would re-fire every time a cart refresh
   * replaces the section markup, racing the very request that caused it, so the
   * element never reconciles itself. It is driven from here instead: once on
   * load, and again whenever the theme reports that the cart changed.
   *
   * Once per load was not enough. The drawer never reloads, so a cart that went
   * wrong while it was open stayed wrong all the way to checkout.
   */
  let firstRun = true;

  function reconcileNow() {
    if (inFlight) return;
    const element = document.querySelector("shipping-protection");
    if (element && typeof element.reconcile === "function") element.reconcile();
  }

  function reconcileOnLoad() {
    if (!firstRun) return;
    firstRun = false;
    reconcileNow();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcileOnLoad);
  } else {
    reconcileOnLoad();
  }

  // The literal is the theme's PUB_SUB_EVENTS.cartUpdate. Reading the constant
  // would tie this file to global.js having been parsed first, and it is a
  // plain `const`, so it is not reachable through `window` either.
  if (
    typeof webvista !== "undefined" &&
    typeof webvista.subscribe === "function"
  ) {
    webvista.subscribe("cart-update", reconcileNow);
  }
})();
